import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';

import CategoryPicker from './CategoryPicker';
import WeeklyScheduleEditor from './WeeklyScheduleEditor';
import { getSchedules, saveSchedules } from '../utils/api';

export default function ScheduleManager({
  categories,
  onCategoriesChange,
  refreshKey = 0,
  showHeading = true,
}) {
  const [schedules, setSchedules] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getSchedules()
      .then((scheduleData) => {
        if (alive) setSchedules(scheduleData.map((schedule) => ({ ...schedule })));
      })
      .catch((err) => {
        if (alive) setError(err.data?.message || err.message || 'Unable to load schedules.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [refreshKey]);

  useEffect(() => {
    const availableIds = new Set(categories.map((category) => category.id));
    setSchedules((current) => current.filter((schedule) => (
      schedule.category_id == null || availableIds.has(schedule.category_id)
    )));
    if (activeCategoryId != null && !availableIds.has(activeCategoryId)) {
      setActiveCategoryId(null);
    }
  }, [activeCategoryId, categories]);

  const categoriesById = useMemo(() => new Map(
    categories.map((category) => [category.id, category]),
  ), [categories]);
  const selectedCategory = activeCategoryId == null
    ? null
    : categoriesById.get(activeCategoryId);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await saveSchedules(schedules);
      const updatedSchedules = await getSchedules();
      setSchedules(updatedSchedules.map((schedule) => ({ ...schedule })));
      setSaved(true);
    } catch (err) {
      setError(err.data?.message || err.message || 'Failed to save schedules. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      sx={(theme) => ({
        p: { xs: 1.5, sm: 2.5 },
        overflow: 'hidden',
        border: `2px solid ${theme.palette.secondary.main}`,
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Stack spacing={2}>
        {showHeading && (
          <Box>
            <Typography variant="h5" sx={{ color: 'primary.main' }}>Weekly schedule</Typography>
            <Typography variant="body2" sx={{ color: 'secondary.main', mt: 0.75 }}>
              Choose a category, then add its focused time to the calendar.
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}
        {saved && <Alert severity="success">Schedule updated.</Alert>}

        {loading ? (
          <Typography>Loading schedules...</Typography>
        ) : (
          <>
            <Box sx={{ maxWidth: 460 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'secondary.main' }}>
                Draw new time blocks as
              </Typography>
              <CategoryPicker
                idPrefix="schedule-active-category"
                categories={categories}
                onCategoriesChange={onCategoriesChange}
                value={activeCategoryId}
                onChange={(categoryId) => {
                  setActiveCategoryId(categoryId);
                  setSaved(false);
                }}
              />
            </Box>

            <WeeklyScheduleEditor
              schedules={schedules}
              onChange={(nextSchedules) => {
                setSchedules(nextSchedules);
                setSaved(false);
              }}
              createSchedule={({ id, day, start, end }) => ({
                id,
                category_id: activeCategoryId,
                category: selectedCategory?.name || 'no-category',
                category_color: selectedCategory?.color || null,
                days: [day],
                start,
                end,
              })}
              getScheduleColor={(schedule) => (
                categoriesById.get(schedule.category_id)?.color
                || schedule.category_color
                || '#00E5FF'
              )}
              getScheduleLabel={(schedule) => (
                categoriesById.get(schedule.category_id)?.name
                || (schedule.category === 'no-category' ? 'No category' : schedule.category)
              )}
              isScheduleReadOnly={(schedule) => schedule.category_id !== activeCategoryId}
              getScheduleGroupKey={(schedule) => schedule.category_id ?? 'uncategorized'}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleSave}
                variant="contained"
                color="secondary"
                disabled={saving}
                sx={(theme) => ({
                  m: 0,
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: 220,
                  color: theme.palette.background.default,
                  boxShadow:
                    `0 5px 0 ${darken(theme.palette.secondary.main, 0.5)}, ` +
                    `0 0 16px ${alpha(theme.palette.secondary.main, 0.35)}`,
                  '&:active': {
                    transform: 'translateY(3px)',
                    boxShadow: `0 2px 0 ${darken(theme.palette.secondary.main, 0.5)}`,
                  },
                })}
              >
                {saving ? 'Updating...' : 'Update schedules'}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
}
